import type { CSSProperties } from "react";

/**
 * Design tokens — adapted from admin-frontend's `src/theme.ts` so this app
 * reads as part of the same platform rather than a bespoke internal tool.
 * Keep values in sync with admin-frontend's palette when that file changes.
 */
export const theme = {
  primaryColor: "#547AC1", // mid blue — primary actions, active states
  ink: "#0A2540", // deep navy — headings / strongest text
  muted: "#697386", // slate — secondary text, labels
  text: "#425466", // slate — primary UI body text
  secondaryColor: "#10b981", // emerald-500 — positive, success highlights
  lines: "#d3d9e0", // darker hairline — borders, dividers
  background: "#f6f7f9", // subtle cool-grey — page background
  surfaceBright: "#fbfcfd", // near-white — inactive pill background
  accentColor: "#EBF5FB", // light blue tint — hover states, subtle backgrounds
  errorColor: "#ef4444", // red-500 — errors, destructive actions
  link: "#263C80", // dark blue — hyperlinks
};

/** Semantic status color pairs (bg + color) used for badges and inline chips. */
export const statusColors = {
  success: { bg: "#d1fae5", color: "#065f46" }, // active
  successSubtle: { bg: "#f0fdf4", color: "#15803d" },
  warning: { bg: "#fef3c7", color: "#92400e" }, // trialing
  info: { bg: "#dbeafe", color: "#1e40af" },
  pending: { bg: "#ede9fe", color: "#5b21b6" },
  error: { bg: "#fee2e2", color: "#991b1b" }, // cancelled / past_due
  neutral: { bg: "#f1f5f9", color: "#475569" }, // unknown / not set
} as const;

/** Border radius scale. */
export const radius = {
  sm: 4,
  md: 8,
  lg: 12,
} as const;

/** Box shadow scale — flat design, no elevation shadows. */
export const shadow = {
  card: "none",
  md: "none",
  lg: "none",
} as const;

/** Standard card container style — border, radius, shared across all page sections. */
export const cardStyle: CSSProperties = {
  backgroundColor: "#ffffff",
  border: `1px solid ${theme.lines}`,
  borderRadius: radius.md,
  boxShadow: shadow.card,
};
